import React from "react";
import PropTypes from "prop-types";
import c from "classnames";
import Modal from "react-bootstrap/Modal";
import styles from "./styles.module.scss";
import {FormattedMessage} from "react-intl";
import CreateNoteDialog from "~/components/CreateNoteDialog/CreateNoteDialog";
import Button from "react-bootstrap/Button";
import Nav from "react-bootstrap/Nav";
import MatchDetailsOverview from "~/components/MatchDetailsOverview";
import config from "~/config";

export default class MatchDetailsDialog extends React.PureComponent {
    static propTypes = {
        match: PropTypes.object.isRequired,
        extraPropertiesOrganization: PropTypes.object.isRequired,
        extraPropertyPhoneNumbers: PropTypes.arrayOf(PropTypes.string).isRequired,
        title: PropTypes.node.isRequired,
        forwardLabel: PropTypes.node.isRequired,
        forwardSubLabel: PropTypes.node,
        reverseLabel: PropTypes.node.isRequired,
        hideForwardColumn: PropTypes.bool.isRequired,
        getMatchDisplayValue: PropTypes.func.isRequired,
        noteTypes: PropTypes.arrayOf(
            PropTypes.shape({
                value: PropTypes.any.isRequired,
                label: PropTypes.string.isRequired,
            })
        ),
        externalDetailsTabUrlFetcher: PropTypes.func,
        externalDetailsTabDefaultActive: PropTypes.bool.isRequired,
        defaultNoteType: PropTypes.any,
        onHide: PropTypes.func,
        onCreateNote: PropTypes.func,
    };

    static defaultProps = {
        forwardLabel: "",
        hideForwardColumn: false,
        extraPropertyPhoneNumbers: [],
        externalDetailsTabDefaultActive: false,
    };

    constructor(props) {
        super(props);

        this.state = {
            activeTab: props.externalDetailsTabDefaultActive ? "externalDetails" : "matchDetails",
            externalDetailsUrlPromise: undefined,
            externalDetailsUrl: undefined,
            showingNoteDialog: false,
            submittingNote: false,
            wasNoteError: false,
        };

        this.unmounted = false;
    }

    componentDidMount() {
        if (this.state.activeTab === "externalDetails") {
            this.fetchExternalDetailsUrl();
        }
    }

    componentWillUnmount() {
        this.unmounted = true;
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (
            prevState.activeTab !== "externalDetails" &&
            this.state.activeTab === "externalDetails"
        ) {
            this.fetchExternalDetailsUrl();
        }
    }

    render() {
        const {onHide, onCreateNote} = this.props;

        return (
            <Modal
                size="lg"
                show={true}
                centered
                onHide={onHide}
                scrollable={true}
                dialogClassName={styles.dialog}
            >
                <Modal.Header closeButton={true} className={styles.modalHeader}>
                    {this.renderTabs()}
                    {onCreateNote !== undefined && !config("ui.disableNotes") && (
                        <Button
                            size="sm"
                            onClick={this.handleOpenNoteDialog}
                            className={styles.addNote}
                        >
                            <FormattedMessage id="detailsModal.addNote" />
                        </Button>
                    )}
                </Modal.Header>
                {this.renderActiveTab()}
            </Modal>
        );
    }

    renderTabs() {
        const {externalDetailsTabUrlFetcher, title} = this.props;
        const {activeTab} = this.state;

        return (
            <Nav id="external-details-tabs" variant="tabs" className={styles.navBar}>
                <Nav.Item>
                    <div className={styles.navTitle}>{title}</div>
                </Nav.Item>
                <Nav.Item onClick={this.handleMatchDetailsTabClick}>
                    <Nav.Link className={c({active: activeTab === "matchDetails"})}>
                        <FormattedMessage id="detailsModal.tabs.matchDetails" />
                    </Nav.Link>
                </Nav.Item>
                {externalDetailsTabUrlFetcher && (
                    <Nav.Item onClick={this.handleExternalDetailsTabClick}>
                        <Nav.Link className={c({active: activeTab === "externalDetails"})}>
                            <FormattedMessage id="detailsModal.tabs.externalDetails" />
                        </Nav.Link>
                    </Nav.Item>
                )}
            </Nav>
        );
    }

    renderActiveTab() {
        const {activeTab} = this.state;

        return (
            <React.Fragment>
                {activeTab === "matchDetails" && (
                    <Modal.Body>{this.renderMatchDetails()}</Modal.Body>
                )}
                <Modal.Body
                    className={c(styles.externalContentBody, {
                        [styles.softHidden]: activeTab !== "externalDetails",
                    })}
                >
                    {this.renderExternalDetails()}
                </Modal.Body>
            </React.Fragment>
        );
    }

    renderMatchDetails() {
        const {
            match,
            extraPropertiesOrganization,
            extraPropertyPhoneNumbers,
            forwardLabel,
            forwardSubLabel,
            reverseLabel,
            hideForwardColumn,
            getMatchDisplayValue,
            externalDetailsTabUrlFetcher,
            externalDetailsTabDefaultActive,
        } = this.props;

        return (
            <React.Fragment>
                <MatchDetailsOverview
                    match={match}
                    extraPropertiesOrganization={extraPropertiesOrganization}
                    extraPropertyPhoneNumbers={extraPropertyPhoneNumbers}
                    forwardLabel={forwardLabel}
                    forwardSubLabel={forwardSubLabel}
                    reverseLabel={reverseLabel}
                    hideForwardColumn={hideForwardColumn}
                    getMatchDisplayValue={getMatchDisplayValue}
                    externalDetailsTabUrlFetcher={externalDetailsTabUrlFetcher}
                    externalDetailsTabDefaultActive={externalDetailsTabDefaultActive}
                />
                {this.renderCreateNoteDialog()}
            </React.Fragment>
        );
    }

    renderExternalDetails() {
        const {externalDetailsUrl} = this.state;

        return (
            <div className={c(styles.externalFrameContainer)}>
                {externalDetailsUrl && (
                    <iframe src={externalDetailsUrl} className={styles.externalFrame} />
                )}
            </div>
        );
    }

    fetchExternalDetailsUrl() {
        const {externalDetailsTabUrlFetcher, match} = this.props;
        const {externalDetailsUrlPromise} = this.state;

        if (externalDetailsUrlPromise === undefined) {
            const promise = externalDetailsTabUrlFetcher(match.id);

            promise.then(url => {
                if (!this.unmounted) {
                    this.setState({externalDetailsUrl: url});
                }
            });

            this.setState({externalDetailsUrlPromise: promise});
        }
    }

    renderCreateNoteDialog() {
        const {match, noteTypes, defaultNoteType} = this.props;
        const {showingNoteDialog, submittingNote, wasNoteError} = this.state;

        if (!showingNoteDialog) return;

        return (
            <CreateNoteDialog
                subjectId={match.id}
                subjectName="Name"
                noteTypes={noteTypes}
                defaultNoteType={defaultNoteType}
                submitting={submittingNote}
                wasError={wasNoteError}
                onSubmit={this.handleSubmitNote}
                onCancel={this.handleCancelNote}
            />
        );
    }

    handleMatchDetailsTabClick = () => {
        this.setState({activeTab: "matchDetails"});
    };

    handleExternalDetailsTabClick = () => {
        this.setState({activeTab: "externalDetails"});
    };

    handleOpenNoteDialog = () => {
        this.setState({showingNoteDialog: true});
    };

    handleCancelNote = () => {
        this.setState({showingNoteDialog: false});
    };

    handleSubmitNote = (...args) => {
        const {onCreateNote} = this.props;

        this.setState({submittingNote: true});

        onCreateNote(...args)
            .then(() => {
                this.setState({submittingNote: false, showingNoteDialog: false});
            })
            .catch(e => {
                this.setState({submittingNote: false, wasNoteError: true});
                console.error(e);
            });
    };
}
